import { FunctionsHttpError, SupabaseClient } from '@supabase/supabase-js';
import { BackendErrorResponse } from '@/types/response/BackendErrorResponse';
import type { Itinerary } from '$/models/Itinerary.model.ts';
import type { CodyChangeResult } from '$/dto/CodyChangeResult.dto.ts';
import RefinementError, { type RefinementError as RefinementErrorCode } from '$/_const/trip/RefinementError.ts';

export class RefinementRequestError extends Error {
  constructor(
    message: string,
    readonly code?: RefinementErrorCode,
    readonly status?: number
  ) {
    super(message);
    this.name = 'RefinementRequestError';
  }
}

const isRefinementErrorCode = (value: unknown): value is RefinementErrorCode =>
  Object.values(RefinementError).includes(value as RefinementErrorCode);

export const toRefinementRequestError = async (error: unknown): Promise<RefinementRequestError> => {
  if (!(error instanceof FunctionsHttpError)) {
    return new RefinementRequestError(
      error instanceof Error ? error.message : 'Refinement request failed.',
      RefinementError.REFINEMENT_FAILED
    );
  }

  const response = error.context as Response;
  const isNoChange = response.status === 422;
  let message: string = isNoChange ? RefinementError.NO_MATERIAL_CHANGE : 'Refinement request failed.';
  let code: RefinementErrorCode | undefined = isNoChange ? RefinementError.NO_MATERIAL_CHANGE : undefined;

  try {
    const body: BackendErrorResponse = await response.json();

    if (Array.isArray(body.errors) && body.errors.length > 0) {
      message = body.errors.join(', ');
    }

    if (isRefinementErrorCode(body.code)) {
      code = body.code;
    }
  } catch (parseError) {
    console.error('Failed to parse refinement error response:', parseError);
  }

  return new RefinementRequestError(message, code, response.status);
};

export const toCodyChangeResult = (response: unknown): CodyChangeResult | null => {
  const body = (response as { data?: unknown } | null)?.data;

  if (!body || typeof body !== 'object') return null;
  if ('outcome' in body) return body as CodyChangeResult;
  if ('id' in body && 'data' in body) {
    return { outcome: 'changed', itinerary: body as Itinerary, change_id: null, summary: '', note: null };
  }

  return null;
};

const invokeRefinement = async (
  client: SupabaseClient,
  functionName: 'refine_trip_cody_suggestion' | 'update_itinerary_day' | 'undo_itinerary_change',
  body: object
): Promise<CodyChangeResult> => {
  const { data, error } = await client.functions.invoke(functionName, { body });

  if (error) {
    throw await toRefinementRequestError(error);
  }

  const result = toCodyChangeResult(data);

  if (!result) {
    throw new RefinementRequestError('Cody did not return an answer.', RefinementError.REFINEMENT_FAILED);
  }

  return result;
};

export default invokeRefinement;
